import clsx from "clsx";
import { useMemo } from "react";
// Material UI
import { GridCellParams, GridColDef, GridGroupingColDefOverride, GridRenderCellParams } from "@mui/x-data-grid-pro";

import { Stack } from "@mui/material";

// Components
import { ExpandCollapseAllButtonHeader } from "@/Layouts/components/TreeGrid/ExpandCollapseAllButtonHeader";
import { CustomGridTreeDataGroupingCell } from "@/Layouts/components/TreeGrid/CustomGridTreeDataGroupingCell";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { ClassNameHints, Issue } from "@/Common/types/tables";
import CoinsGreen from "@/assets/icons/common/Coins.svg";
import CoinsGray from "@/assets/icons/common/Coins-gray.svg";
import Received from "@/assets/icons/dataPages/salesOperations/salesPerformance/received-icon.svg";
import Sent from "@/assets/icons/dataPages/salesOperations/salesPerformance/sent-icon.svg";
import LocalSale from "@/assets/icons/dataPages/salesOperations/salesPerformance/LocalSale.svg";
import { TypographyHeaderProps } from "@/Layouts/components/Tables/DataSmartTable/types";

// Constants
import {
	COLUMN_RESIZE_FACTOR,
	EIGHTY,
	HEADER_CLASS_NAME,
	HEADER_CLASS_NAME_2,
	ONE_HUNDRED_FIFTY,
	ONE_HUNDRED_TWENTY,
} from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";

// Helpers
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { computeProps, getCellClassNameFn } from "@/Common/helpers/Tables";
import {
	baseConfig,
	getBasicColumnWithTooltip,
	getPinnedCellClassName,
	getUniformWidth,
	ifFooterValueGetter,
	isAutoGeneratedFooter,
} from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { measureTextWidth } from "@/Common/helpers/measure";

// Types
import { CustomGridTreeDataGroupingCellProps } from "@/Layouts/components/types";
import { PerformanceItem } from "@/Washouts/types/sales";
import { ISaleIdentifier, ISales } from "@DataPages/types/salesOperations/salesPerformance/models/salesPerformance.ts";

interface IUseSalesPerformanceTableConfiguration {
	totalBackGrossProfitFooter: number | string;
	totalFrontGrossProfitFooter: number | string;
	totalUnitsFooter: number | string;
	rows: ISales[];
	productsFooters: {
		[key: string]: number;
	};
	locationId: string;
}

const groupingColDef: GridGroupingColDefOverride = {
	width: 48,
	maxWidth: 48,
	minWidth: 48,
	resizable: false,
	headerName: "Group",
	renderHeader: () => (
		<Stack alignItems="center" justifyContent="center" className="relative w-7 h-9">
			<ExpandCollapseAllButtonHeader className="left-[1px]" />
		</Stack>
	),
	colSpan: (value) => (!value ? 2 : 1),
	renderCell: (params) => {
		if (isAutoGeneratedFooter(params.id)) {
			return "Total";
		}
		return (
			<div className="pt-[2px]">
				<CustomGridTreeDataGroupingCell {...(params as CustomGridTreeDataGroupingCellProps)} />
			</div>
		);
	},
	getApplyQuickFilterFn: () => null,
};

const baseColumnParameters = (valueParam = "value") => {
	return {
		headerClassName: HEADER_CLASS_NAME_2,
		cellClassName: getCellClassNameFn([], {}, {}),
		renderCell: (params: GridRenderCellParams) => (
			<TypographyCell
				render="absolute"
				params={params}
				issues={[]}
				hints={{ isFunded: false }}
				fontSize={isAutoGeneratedFooter(params?.id) ? "14px" : "12px"}
			>
				{params?.[valueParam as keyof GridRenderCellParams]}
			</TypographyCell>
		),
	};
};

const baseGrossColumnParameters = () => {
	return {
		width: ONE_HUNDRED_TWENTY,
		minWidth: ONE_HUNDRED_TWENTY,
		maxWidth: ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR,
		resizable: false,
		headerClassName: `${HEADER_CLASS_NAME} !text-left`,
		cellClassName: getCellClassNameFn([], { isFunded: false }, {}),
		renderCell: (params: GridRenderCellParams) => (
			<TypographyCell
				render="absolute"
				params={params}
				issues={[]}
				hints={{ isFunded: false }}
				fontSize={isAutoGeneratedFooter(params?.id) ? "14px" : "12px"}
			/>
		),
	};
};

const imageDivDisplaySx = { display: "flex", justifyContent: "center" };
export const renderExternalColumn = (params: GridRenderCellParams) => {
	if (isAutoGeneratedFooter(params?.id)) {
		return (
			<TypographyCell
				hints={{
					isFooter: true,
					noFocus: true,
				}}
				fontSize="14px"
			/>
		);
	}
	const sale = params?.row as ISales;
	const height = "17px";
	const width = "16px";
	if (sale?.origin) {
		let tooltipText =
			sale?.origin?.tooltip ??
			sale?.identifiers?.map?.((identifier: ISaleIdentifier) => identifier?.oemName)?.join?.(", ") ??
			"Local Deal";
		let src;
		switch (String(params?.value).toUpperCase()) {
			case "RECEIVED":
			case "INBOUND":
				src = Received;
				break;
			case "SENT":
			case "OUTBOUND":
				src = Sent;
				break;
			case "LOCAL":
			default:
				src = LocalSale;
				break;
		}
		tooltipText =
			{
				RECEIVED: "In: ",
				INBOUND: "In: ",
				SENT: "Out: ",
				OUTBOUND: "Out: ",
				LOCAL: "",
			}[String(params?.value).toUpperCase()] + tooltipText;
		return (
			<TypographyCell
				tooltipProps={{ title: tooltipText }}
				hasTooltip
				forceTooltip
				sx={{ ...imageDivDisplaySx, width, height, maxHeight: height, maxWidth: width }}
			>
				<img src={src} alt={`${tooltipText} icon`} width={width} height={height} style={{ maxHeight: height, maxWidth: width }} />
			</TypographyCell>
		);
	}
	return (
		<TypographyCell
			tooltipProps={{ title: "Local Deal" }}
			sx={{ ...imageDivDisplaySx, width, height, maxHeight: height, maxWidth: width }}
		>
			<img src={LocalSale} width={width} height={height} alt="Local deal icon" style={{ maxHeight: height, maxWidth: width }} />
		</TypographyCell>
	);
};

export const renderFundedColumn = (params: GridRenderCellParams) => {
	if (isAutoGeneratedFooter(params?.id)) {
		return (
			<TypographyCell render="absolute" hints={{ isFooter: true, noWrap: true, noFocus: true, isNegative: false }} fontSize="14px">
				Total
			</TypographyCell>
		);
	}
	const title = params?.row?.funded ? "Funded" : "Unfunded";
	return (
		<TypographyCell tooltipProps={{ title }} sx={{ ...imageDivDisplaySx }}>
			<img src={params?.row?.funded ? CoinsGreen : CoinsGray} width="16px" height="16px" alt={`${title} icon`} />
		</TypographyCell>
	);
};

const iconColumnWidth = 38;

export const getSalesPerformanceRowsWithSearchValues = (rows: (ISales | PerformanceItem)[]) =>
	rows.map((row) => {
		return {
			...row,
			origin: {
				...row.origin,
				icon: resolveExternalColumnValue(row),
			},
		};
	}) || [];

export const resolveExternalColumnValue = (row: ISales | PerformanceItem) => {
	const sale = row;
	if (sale?.origin?.icon === "RECEIVED" || sale?.origin?.icon === "Inbound") {
		return "Inbound";
	}
	if (sale?.origin?.icon === "SENT" || sale?.origin?.icon === "Outbound") {
		return "Outbound";
	}
	return "Local";
};

export const externalColumn: GridColDef = {
	field: "external",
	headerName: "Origin",
	...getUniformWidth(iconColumnWidth, 0, false),
	resizable: false,
	headerClassName: "!p-1 !overflow-visible",
	cellClassName: (params: GridCellParams) => getPinnedCellClassName(params, "", {}, ""),
	type: "singleSelect",
	valueOptions: () => ["Inbound", "Outbound", "Local"],
	renderCell: (params: GridRenderCellParams) => {
		return renderExternalColumn(params);
	},
	valueGetter: (_value: boolean, row: unknown) => resolveExternalColumnValue(row as ISales),
	renderHeader: () => <></>,
	filterable: true,
};

export const fundedColumn: GridColDef = {
	field: "funded",
	headerName: "Funded",
	...getUniformWidth(iconColumnWidth, 0, false),
	headerClassName: "!p-1 !overflow-visible",
	cellClassName: (params: GridCellParams) => getPinnedCellClassName(params, "", {}, "!overflow-visible !z-10"),
	renderCell: (params) => {
		return renderFundedColumn(params);
	},
	renderHeader: () => <></>,
	type: "singleSelect",
	valueOptions: ["Funded", "Unfunded"],
	valueGetter: (_value: boolean, row: unknown) => {
		const sale = row as ISales;
		return sale?.funded ? "Funded" : "Unfunded";
	},
};

const baseColumnDefinitions = (): GridColDef[] => {
	return [
		{
			field: "_id",
			headerName: "ID",
			filterable: false,
		},
		{
			field: "sourceLocationId",
			headerName: "Source Location Id",
			filterable: false,
		},
		fundedColumn,
		externalColumn,
		{
			field: "salesEmployeeId",
			headerName: "Sales Employee Id",
			width: 10,
			filterable: false,
		},
		{
			field: "salesEmployeeName",
			headerName: "Sales Employee",
			headerClassName: `${HEADER_CLASS_NAME_2} `,
			...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR, true),
			cellClassName: getPinnedCellClassName,
			renderCell: (params) => {
				let value = params.row.salesEmployeeName || params.row.salesEmployeeId;
				if (params.row.salesEmployeeId === "9999") {
					value = "HOUSE";
				}
				return getBasicColumnWithTooltip(value, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
			align: "left",
		},
		{
			field: "saleDate",
			headerName: "Sale Date",
			type: "date",
			align: "left",
			headerClassName: HEADER_CLASS_NAME_2,
			width: ONE_HUNDRED_TWENTY,
			resizable: false,
			cellClassName: getPinnedCellClassName,
			valueGetter: (value) => value && getDateInUTC(value),
			valueFormatter: (value) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
		},
		{
			field: "dealNumber",
			headerName: "Deal #",
			...getUniformWidth(EIGHTY, COLUMN_RESIZE_FACTOR),
			resizable: false,
			...baseColumnParameters(),
			cellClassName: getPinnedCellClassName,
			align: "right",
			renderCell: (params: GridRenderCellParams) => <TypographyCell params={params} />,
		},
		{
			field: "frontGrossProfit",
			headerName: "Total Front Gross",
			...baseGrossColumnParameters(),
			type: "number",
			cellClassName: getPinnedCellClassName,
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
		{
			field: "backGrossProfit",
			headerName: "Total Back Gross",
			...baseGrossColumnParameters(),
			type: "number",
			cellClassName: getPinnedCellClassName,
			renderHeader: (params) => <TypographyHeader params={params} left />,
		},
		{
			field: "__empty",
			headerName: "",
			sortable: false,
			resizable: false,
			filterable: false,
			width: 12,
			headerClassName: "!z-10 !bg-white !border-none !p-0",
			cellClassName: () => "empty-row !bg-white !border-none !p-0",
			renderCell: () => <TypographyCell className={"!bg-blue"} />,
		},
		{
			field: "financeEmployeeId",
			headerName: "Finance Employee Id",
			width: 10,
			filterable: false,
		},
		{
			field: "financeEmployeeName",
			headerName: "Finance Employee",
			headerClassName: HEADER_CLASS_NAME_2,
			...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR, true),
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => {
				let value = params.row.financeEmployeeName || params.row.financeEmployeeId;
				if (params.row.financeEmployeeId === "9999") {
					value = "HOUSE";
				}
				return getBasicColumnWithTooltip(value, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
			align: "left",
		},
		{
			field: "salesManagerId",
			headerName: "Sales Manager Id",
			width: 10,
			filterable: false,
		},
		{
			field: "salesManagerName",
			headerName: "Sales Manager",
			headerClassName: HEADER_CLASS_NAME_2,
			...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR, true),
			cellClassName: getCellClassNameFn([], {}, {}),
			renderCell: (params) => {
				return getBasicColumnWithTooltip(
					params.row.salesManagerName,
					params,
					params.colDef.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR
				);
			},
			align: "left",
		},
		{
			field: "customerName",
			headerName: "Customer",
			...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR, true),
			...baseColumnParameters(),
			align: "left",
			renderCell: (params) => {
				return getBasicColumnWithTooltip(undefined, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
		},
		{
			field: "stockNumber",
			headerName: "Stock #",
			width: EIGHTY,
			resizable: false,
			...baseColumnParameters(),
			align: "right",
			renderCell: (params) => {
				return getBasicColumnWithTooltip(undefined, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
		},
		{
			field: "description",
			headerName: "Description",
			...getUniformWidth(ONE_HUNDRED_FIFTY, COLUMN_RESIZE_FACTOR, true),
			...baseColumnParameters("formattedValue"),
			align: "left",
			renderCell: (params) => {
				return getBasicColumnWithTooltip(undefined, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
		},
		{
			field: "saleType",
			headerName: "Sale Type",
			width: EIGHTY,
			resizable: false,
			...baseColumnParameters("formattedValue"),
			renderCell: (params) => {
				return getBasicColumnWithTooltip(undefined, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
			align: "left",
		},
		{
			field: "dealType",
			headerName: "Deal Type",
			width: EIGHTY,
			resizable: false,
			...baseColumnParameters("formattedValue"),
			renderCell: (params) => {
				return getBasicColumnWithTooltip(undefined, params, params?.colDef?.width ?? ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR);
			},
			align: "left",
		},
		{
			field: "units",
			headerName: "Units",
			width: EIGHTY,
			resizable: false,
			type: "number",
			...baseColumnParameters("formattedValue"),
		},
	];
};

type TConfigurableFields = keyof ISales;

const augmentColumnDefinitions =
	(dynamicConfig: Partial<Record<string, Partial<GridColDef>>>) =>
	(column: GridColDef): GridColDef => {
		const dynamicColumnConfig = dynamicConfig[column.field as TConfigurableFields];
		return {
			...column,
			...dynamicColumnConfig,
		};
	};

const productColumnRenderCell = (
	params: GridRenderCellParams,
	key: "frontProducts" | "backProducts",
	productsFooters: { [key: string]: number }
) => {
	let value = params?.row?.[key]?.find?.(
		(frontProduct: { _id?: string; product: string; value: number }) => frontProduct?.product === params.field
	)?.value;
	const isFooter = isAutoGeneratedFooter(params.id);
	if (isFooter) {
		value = productsFooters[params?.field] || 0;
	}
	return (
		<TypographyCell
			render="absolute"
			params={{ ...params, value } as GridRenderCellParams}
			issues={[]}
			hints={{
				isFunded: false,
				isSubTotalRow: isFooter,
				noWrap: isFooter,
				noFocus: isFooter,
				isNegative: value < 0,
			}}
			fontSize={isFooter ? "14px" : "12px"}
			height={"25px !important"}
		/>
	);
};

export const resolveProductLineClamp = (productName = "", width = 100): TypographyHeaderProps["lineClamp"] => {
	const wordsInProductName = productName.split(" ");
	if (wordsInProductName?.length === 1 && measureTextWidth(productName) >= width) {
		return "";
	}
	return "2";
};

export function productCellClassNameFn(
	issues: Issue[] = [],
	hintsOverride: Partial<ClassNameHints> = {},
	classes: {
		footer?: string;
		base?: string;
	} = { footer: "", base: "" },
	reference = "",
	productsFooters: { [key: string]: number } = {}
) {
	return (params: GridCellParams) => {
		let value = params?.value;
		let hints = { ...computeProps(reference, params, issues), ...hintsOverride };
		if (hints?.isFooter) {
			value = productsFooters[params?.field] || 0;
			hints = { ...computeProps(reference, { ...params, value }, issues), ...hintsOverride };
		}
		return clsx({
			"!border-none": true,
			[`${classes.base ?? ""}`]: true,
			[`${classes.footer ?? ""}`]: hints.isFooter,
			"!bg-[var(--primary-color)] !text-white": !hints.isNegative && hints.isFooter,
			"!bg-[var(--light-gray-color-v2)] !text-[var(--text-alert-color)]": hints.isNegative && hints.isFooter,
			"!overflow-visible !relative": hints.noWrap && hints.isFooter,
			"no-focus": hints.noFocus,
			"table-noWrap": hints.noWrap,
			"simple-negative-value": hints.isNegative,
			[`cell-color-${hints.colorIssued?.replace("#", "")}`]: hints.colorIssued,
		});
	};
}

const getExpandedColumns = (rows: ISales[], expanded: boolean, productsFooters: { [key: string]: number }): GridColDef[] => {
	if (!expanded) {
		return [];
	}
	const productsColumns: GridColDef[] = [];
	rows.forEach((sale) => {
		["frontProducts", "backProducts"].forEach((key) => {
			sale[key as "frontProducts" | "backProducts"]?.forEach((product) => {
				if (!productsColumns.find((column) => column.field === product.product)) {
					productsColumns.push({
						field: product.product,
						headerName: product.product,
						type: "number",
						...baseGrossColumnParameters(),
						...getUniformWidth(ONE_HUNDRED_TWENTY, COLUMN_RESIZE_FACTOR),
						valueGetter: (_value: string | number, row: ISales, column: GridColDef) => {
							return row?.[key as "frontProducts" | "backProducts"]?.find?.(
								(frontProduct: { _id?: string; product: string; value: number }) => frontProduct?.product === column.field
							)?.value;
						},
						renderCell: (params: GridRenderCellParams) => {
							return productColumnRenderCell(params, key as "frontProducts" | "backProducts", productsFooters);
						},
						cellClassName: productCellClassNameFn([], { isFunded: false, isMoney: true }, {}, "", productsFooters),
						renderHeader: (params) => (
							<TypographyHeader params={params} left lineClamp={resolveProductLineClamp(product.product)} />
						),
					});
				}
			});
		});
	});
	return productsColumns;
};

export const useSalesPerformanceTableColumns = ({
	rows,
	productsFooters,
	totalBackGrossProfitFooter,
	totalFrontGrossProfitFooter,
	totalUnitsFooter,
}: IUseSalesPerformanceTableConfiguration) => {
	const expandedColumns = getExpandedColumns(rows, true, productsFooters);
	let columnsToUse: GridColDef[] = baseColumnDefinitions();
	const unitsColumn = columnsToUse.find((column) => column.field === "units");
	columnsToUse = columnsToUse.filter((column) => column.field !== "units");
	columnsToUse = [...columnsToUse, ...expandedColumns];
	if (unitsColumn) {
		columnsToUse.push(unitsColumn);
	}

	const columns = useMemo<GridColDef[]>(
		() =>
			columnsToUse
				.map(
					augmentColumnDefinitions({
						__isFunded: {
							valueGetter: (_value, sale: ISales) => {
								if (isAutoGeneratedFooter(sale?._id ?? "") || !sale?.funded) {
									return "";
								}
								return sale?.funded ? "Funded" : "Unfunded";
							},
						},
						units: {
							valueGetter: ifFooterValueGetter(totalUnitsFooter),
						},
						frontGrossProfit: {
							valueGetter: ifFooterValueGetter(totalFrontGrossProfitFooter),
						},
						backGrossProfit: {
							valueGetter: ifFooterValueGetter(totalBackGrossProfitFooter),
						},
					})
				)
				.map((customConfig) => ({
					...baseConfig,
					...customConfig,
					minWidth: customConfig.minWidth ?? customConfig.width,
					maxWidth: customConfig.maxWidth ?? customConfig.width,
				})),
		[rows, productsFooters, totalBackGrossProfitFooter, totalFrontGrossProfitFooter, totalUnitsFooter]
	);

	return useMemo(
		() => ({
			columns,
			groupingColDef,
		}),
		[columns]
	);
};
